// Pinyin search utility using dictionary lookup (accurate across all browsers)
// Supports: progressive prefix ("s"/"sh"/"she"/"sheng" all match 生),
// initials ("shq" → 生活群), and full pinyin ("shenghuo" → 生活群)

import PINYIN_DATA from './pinyinData';

const RE_LATIN = /^[a-zA-Z]+$/;
const RE_CJK = /[\u4e00-\u9fa5]/;

let charMap: Map<string, string> | undefined;

function getCharMap() {
  if (charMap) return charMap;
  charMap = new Map();
  for (const entry of PINYIN_DATA.split('|')) {
    // Each entry: "pinyinCHARS" — pinyin letters followed by CJK chars
    const match = entry.match(/^([a-z]+)([\s\S]+)$/);
    if (!match) continue;
    const py = match[1];
    for (const ch of match[2]) {
      // Only set first occurrence (most common reading)
      if (!charMap.has(ch)) {
        charMap.set(ch, py);
      }
    }
  }
  return charMap;
}

function charToPinyin(ch: string): string | undefined {
  return getCharMap().get(ch);
}

function toPinyinArray(text: string): string[] {
  const result: string[] = [];
  for (const ch of text) {
    if (RE_CJK.test(ch)) {
      const py = charToPinyin(ch);
      if (py) result.push(py);
    }
  }
  return result;
}

// Match query (latin letters) against text containing CJK characters
// Progressive prefix: "s", "sh", "she", "sheng" all match 生
// Initials: "shq" matches 生活群
// Full pinyin: "shenghuo" matches 生活群
export default function matchPinyin(text: string, query: string): boolean {
  if (!RE_LATIN.test(query) || !RE_CJK.test(text)) return false;

  const q = query.toLowerCase();
  const pinyins = toPinyinArray(text);
  if (!pinyins.length) return false;

  // 1. Progressive prefix from any starting character:
  //    "s"/"sh"/"she"/"sheng"/"shenghuo"/"shenghuoqun" all match 生活群
  for (let i = 0; i < pinyins.length; i++) {
    if (matchProgressivePrefix(pinyins, i, q)) return true;
  }

  // 2. Initial letter match: "shq" matches 生(s)活(h)群(q)
  const initials = pinyins.map((py) => py[0]).join('');
  if (initials.includes(q)) return true;

  return false;
}

// Check if query matches pinyins starting at index `start` with progressive prefix support.
// For pinyins ["sheng", "huo", "qun"] and query "she":
//   - "sheng" starts with "she" → true (partial match within first syllable)
// For query "shengh":
//   - consume "sheng" fully, remaining "h" → "huo" starts with "h" → true
function matchProgressivePrefix(pinyins: string[], start: number, query: string): boolean {
  let remaining = query;

  for (let i = start; i < pinyins.length; i++) {
    if (!remaining) return true; // query fully consumed

    const py = pinyins[i];

    if (py.startsWith(remaining)) {
      // Remaining query fits within this syllable (partial match)
      return true;
    }

    if (remaining.startsWith(py)) {
      // This syllable is fully consumed, continue with next
      remaining = remaining.slice(py.length);
    } else {
      return false;
    }
  }

  return !remaining; // true if query fully consumed
}
